import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { combineUtcDateAndTime, defaultCalendarToUtc } from './growth-datetime.util';
import { GrowthOptimizationService, GrowthOptimizer, GrowthOptimizerAutomationSetting } from './growth-optimization.service';

@Component({
  standalone: true,
  selector: 'ain-growth-optimizer-automation',
  imports: [
    FormsModule,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    ButtonModule,
    SelectModule,
    InputTextModule
  ],
  template: `
    <ain-page [eyebrow]="t('growth.eyebrow')" [title]="t('growth.optimization.automation.title')" [description]="t('growth.optimization.automation.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.optimization.automation.help.title'"
        [descriptionKey]="'growth.optimization.automation.help.description'"
        [bulletKeys]="[
          'growth.optimization.automation.help.bullets.defaultSafe',
          'growth.optimization.automation.help.bullets.evidenceRequired',
          'growth.optimization.automation.help.bullets.autoInLimit'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card">
          <div class="grid">
            <ain-form-field [label]="t('growth.optimization.automation.fields.optimizer')">
              <p-select [options]="optimizerOptions()" [(ngModel)]="form.optimizerId" optionLabel="label" optionValue="value" appendTo="body" (ngModelChange)="loadCurrentSetting()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.automation.fields.currentLevel')">
              <input pInputText [ngModel]="currentSetting()?.automationLevel ?? '-'" readonly />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.automation.fields.requestedLevel')">
              <p-select [options]="levelOptions" [(ngModel)]="form.requestedLevel" optionLabel="label" optionValue="value" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.automation.fields.effectiveDate')">
              <ain-date-input [(ngModel)]="form.effectiveDateUtc" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.automation.fields.approvedBy')">
              <input pInputText [(ngModel)]="form.evidence.approvedBy" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.automation.fields.reason')">
              <input pInputText [(ngModel)]="form.evidence.reason" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.optimization.automation.fields.ticketRef')">
              <input pInputText [(ngModel)]="form.evidence.ticketRef" />
            </ain-form-field>
          </div>
          <div class="actions">
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .actions { margin-top: 8px; display: flex; gap: 8px; }
  `]
})
export class GrowthOptimizerAutomationComponent {
  private readonly service = inject(GrowthOptimizationService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly optimizers = signal<GrowthOptimizer[]>([]);
  readonly currentSetting = signal<GrowthOptimizerAutomationSetting | null>(null);

  form = {
    optimizerId: '',
    requestedLevel: 'HumanApproval',
    effectiveDateUtc: defaultCalendarToUtc(),
    evidence: {
      approvedBy: '',
      reason: '',
      ticketRef: ''
    }
  };

  readonly levelOptions = [
    { label: 'HumanApproval', value: 'HumanApproval' },
    { label: 'Advisory', value: 'Advisory' },
    { label: 'Assisted', value: 'Assisted' },
    { label: 'AutoInLimit', value: 'AutoInLimit' }
  ];

  constructor() {
    this.loadOptimizers();
  }

  t(key: string): string { return this.i18n.translate(key); }

  optimizerOptions() {
    return this.optimizers().map(item => ({ label: `${item.name} (${item.optimizerKey})`, value: item.id }));
  }

  loadOptimizers(): void {
    this.loading.set(true);
    this.service.listOptimizers().subscribe({
      next: rows => {
        this.optimizers.set(rows);
        if (!this.form.optimizerId && rows.length > 0) this.form.optimizerId = rows[0].id;
        this.loadCurrentSetting();
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  loadCurrentSetting(): void {
    if (!this.form.optimizerId) return;
    this.service.getAutomationLevel(this.form.optimizerId).subscribe({
      next: row => this.currentSetting.set(row),
      error: () => this.currentSetting.set(null)
    });
  }

  save(): void {
    if (!this.form.optimizerId) return;
    const approvalEvidenceJson = JSON.stringify({
      approvedBy: this.form.evidence.approvedBy,
      reason: this.form.evidence.reason,
      ticketRef: this.form.evidence.ticketRef
    });

    this.service.updateAutomationLevel(this.form.optimizerId, {
      requestedLevel: this.form.requestedLevel,
      effectiveFromUtc: combineUtcDateAndTime(this.form.effectiveDateUtc, '09:00') ?? new Date().toISOString(),
      approvalEvidenceJson
    }).subscribe({
      next: row => this.currentSetting.set(row)
    });
  }
}
